import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/App";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ExternalLink, Search, ArrowRight } from "lucide-react";
import ManufacturerCombobox from "@/pages/ManufacturerCombobox";
import MethodGlyph from "@/components/MethodGlyph";
import { vehicleTypeLabel, fuelLabel } from "@/lib/labels";
import ToolLogo from "@/components/ToolLogo";
import GroupedCompatibilityList from "@/components/GroupedCompatibilityList";
import NewBadge from "@/components/NewBadge";
import VehicleTypeIcon from "@/components/VehicleTypeIcon";

const PAGE_SIZE = 30;

export default function Compatibility() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get("search") || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(initialSearch);
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [vehicleType, setVehicleType] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [ecuTypeFilter, setEcuTypeFilter] = useState("all");
  const [ecuMakerFilter, setEcuMakerFilter] = useState("all");
  const [manufacturer, setManufacturer] = useState(null);
  const [facets, setFacets] = useState({
    vehicle_types: [],
    methods: [],
    ecu_types: [],
    ecu_makers: [],
    manufacturers: [],
    total: 0,
  });

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (vehicleType !== "all") params.vehicle_type = vehicleType;
      if (methodFilter !== "all") params.method = methodFilter;
      if (ecuTypeFilter !== "all") params.ecu_type = ecuTypeFilter;
      if (ecuMakerFilter !== "all") params.ecu_maker = ecuMakerFilter;
      if (manufacturer) params.manufacturer = manufacturer;
      const { data } = await api.get("/compatibility", { params });
      setItems(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (e) {
      toast.error("Fehler beim Laden der Kompatibilitätsliste");
    } finally {
      setLoading(false);
    }
  }, [page, search, vehicleType, methodFilter, ecuTypeFilter, ecuMakerFilter, manufacturer]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/compatibility/facets");
        setFacets(data);
      } catch (err) {
        console.warn("facets error", err);
      }
    })();
  }, []);

  // Debounce search + reflect in URL query
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
      if (searchInput) {
        setSearchParams({ search: searchInput }, { replace: true });
      } else if (searchParams.get("search")) {
        setSearchParams({}, { replace: true });
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const showingRange = useMemo(() => {
    if (total === 0) return "0";
    const from = (page - 1) * PAGE_SIZE + 1;
    const to = Math.min(page * PAGE_SIZE, total);
    return `${from}–${to}`;
  }, [page, total]);

  return (
    <div data-testid="compat-page" className="space-y-6">
      {/* Hero */}
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <ToolLogo source="autotuner" className="h-8" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center">
          {facets.by_vehicle_type ? null : null}
          <StatCard
            label="Einträge"
            value={facets.total.toLocaleString("de-DE")}
            testid="stat-total"
          />
          <StatCard
            label="Hersteller"
            value={facets.manufacturers?.length ?? 0}
            testid="stat-manufacturers"
          />
          <StatCard
            label="Methoden"
            value={facets.methods?.length ?? 0}
            testid="stat-methods"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-neutral-800 bg-panel p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input
            data-testid="search-input"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Suche Hersteller, Modell, Motor, ECU/TCU, TC1724…"
            className="pl-9 bg-panel border-neutral-800 text-neutral-100 placeholder:text-neutral-500 focus-visible:ring-[#D32222]"
          />
        </div>
        <FilterSelect
          testid="filter-type"
          value={vehicleType}
          onChange={(v) => {
            setVehicleType(v);
            setPage(1);
          }}
          placeholder="Fahrzeugtyp"
          options={[
            { value: "all", label: "Alle Typen" },
            ...(facets.vehicle_types || []).map((v) => ({
              value: v,
              label: vehicleTypeLabel(v),
              icon: <VehicleTypeIcon type={v} className="h-5 w-7" />,
            })),
          ]}
        />
        <FilterSelect
          testid="filter-ecu-type"
          value={ecuTypeFilter}
          onChange={(v) => {
            setEcuTypeFilter(v);
            setPage(1);
          }}
          placeholder="ECU / TCU"
          options={[
            { value: "all", label: "ECU & TCU" },
            { value: "ECU", label: "Nur ECU" },
            { value: "TCU", label: "Nur TCU" },
          ]}
        />
        <FilterSelect
          testid="filter-method"
          value={methodFilter}
          onChange={(v) => {
            setMethodFilter(v);
            setPage(1);
          }}
          placeholder="Read-Methode"
          options={[
            { value: "all", label: "Alle Methoden" },
            ...(facets.methods || []).map((v) => ({
              value: v,
              label: v,
              icon: <MethodGlyph label={v} className="w-8 h-8" />,
            })),
          ]}
        />
        <FilterSelect
          testid="filter-ecu-maker"
          value={ecuMakerFilter}
          onChange={(v) => {
            setEcuMakerFilter(v);
            setPage(1);
          }}
          placeholder="ECU-Hersteller"
          options={[
            { value: "all", label: "Alle ECU-Hersteller" },
            ...(facets.ecu_makers || []).map((v) => ({ value: v, label: v })),
          ]}
        />
        <ManufacturerCombobox
          value={manufacturer}
          onChange={(v) => {
            setManufacturer(v);
            setPage(1);
          }}
          options={facets.manufacturers || []}
        />
      </div>

      <GroupedCompatibilityList
        provider="autotuner"
        params={{
          search: search || undefined,
          vehicle_type: vehicleType !== "all" ? vehicleType : undefined,
          method: methodFilter !== "all" ? methodFilter : undefined,
          ecu_type: ecuTypeFilter !== "all" ? ecuTypeFilter : undefined,
          ecu_maker: ecuMakerFilter !== "all" ? ecuMakerFilter : undefined,
          brand: manufacturer || undefined,
        }}
        renderRow={(row) => <Row row={row} search={search} grouped />}
        emptyText="Keine Einträge gefunden. Passe deine Filter an oder starte den Scraper."
      />

      {/* Table */}
      <div className="hidden rounded-xl border border-neutral-800 bg-panel overflow-hidden">
        {/* Table header */}
        <div className="hidden md:grid grid-cols-12 gap-4 px-4 pt-3 pb-2 text-[11px] uppercase tracking-[0.2em] text-neutral-500 border-b border-neutral-800">
          <div className="col-span-3 lg:col-span-2">Marke</div>
          <div className="col-span-2 lg:col-span-1">Model</div>
          <div className="col-span-2 lg:col-span-4">Motor</div>
          <div className="col-span-2">ECU / TCU</div>
          <div className="col-span-1">Typ</div>
          <div className="col-span-2">Methods</div>
        </div>

        {/* Rows */}
        <div data-testid="compat-list" className="divide-y divide-neutral-800/70">
          {loading && items.length === 0 ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="px-4 py-4 animate-pulse grid grid-cols-12 gap-4"
              >
                <div className="col-span-3 lg:col-span-2 h-6 bg-neutral-900 rounded" />
                <div className="col-span-2 lg:col-span-1 h-6 bg-neutral-900 rounded" />
                <div className="col-span-2 lg:col-span-4 h-6 bg-neutral-900 rounded" />
                <div className="col-span-2 h-6 bg-neutral-900 rounded" />
                <div className="col-span-1 h-6 bg-neutral-900 rounded" />
                <div className="col-span-2 h-6 bg-neutral-900 rounded" />
              </div>
            ))
          ) : items.length === 0 ? (
            <div
              data-testid="empty-state"
              className="px-4 py-16 text-center text-neutral-500"
            >
              Keine Einträge gefunden. Passe deine Filter an oder starte den
              Scraper.
            </div>
          ) : (
            items.map((row, idx) => <Row key={idx} row={row} search={search} />)
          )}
        </div>
      </div>

      {/* Pagination */}
      <div className="hidden flex-wrap items-center justify-between gap-4 pt-2">
        <div className="text-sm text-neutral-500">
          Zeige{" "}
          <span data-testid="range-label" className="text-neutral-200">
            {showingRange}
          </span>{" "}
          von{" "}
          <span data-testid="total-label" className="text-neutral-200">
            {total.toLocaleString("de-DE")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="prev-page"
            variant="outline"
            className="border-neutral-700 bg-panel hover:bg-neutral-800 text-neutral-100"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
          <div className="text-sm text-neutral-400 px-2">
            Seite <span className="text-neutral-100">{page}</span> /{" "}
            <span className="text-neutral-100">
              {totalPages.toLocaleString("de-DE")}
            </span>
          </div>
          <Button
            data-testid="next-page"
            variant="outline"
            className="border-neutral-700 bg-panel hover:bg-neutral-800 text-neutral-100"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
          >
            Weiter <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-neutral-800 bg-panel px-4 py-3 min-w-[110px]"
    >
      <div className="text-xl font-semibold text-[#D32222] tabular-nums">
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
        {label}
      </div>
    </div>
  );
}

function FilterSelect({ testid, value, onChange, placeholder, options }) {
  const selected = options.find((o) => o.value === value);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testid}
        className="w-[190px] h-auto min-h-9 py-1.5 bg-panel border-neutral-800 text-neutral-100 focus:ring-[#D32222]"
      >
        {selected?.icon ? (
          <span className="inline-flex items-center gap-2">
            {selected.icon}
            {selected.label}
          </span>
        ) : (
          <SelectValue placeholder={placeholder} />
        )}
      </SelectTrigger>
      <SelectContent className="bg-panel border-neutral-800 text-neutral-100">
        {options.map((o) => (
          <SelectItem
            key={o.value}
            value={o.value}
            data-testid={`${testid}-opt-${o.value}`}
            className="focus:bg-neutral-800"
          >
            <span className="inline-flex items-center gap-2">
              {o.icon}
              {o.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function Highlight({ text, query }) {
  if (text === undefined || text === null || text === "") return text;
  const str = String(text);
  const tokens = (query || "").split(/\s+/).filter(Boolean).map(escapeRegex);
  if (tokens.length === 0) return str;
  const re = new RegExp(`(${tokens.join("|")})`, "gi");
  const parts = str.split(re);
  if (parts.length === 1) return str;
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <mark
        key={i}
        className="bg-[#D32222]/30 text-[#E13636] rounded-sm px-0.5"
      >
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

function Row({ row, search, grouped = false }) {
  const ecu = row.ecu_tcu || {};
  const model = [row.model, row.version].filter(Boolean).join(" ");
  const navigate = useNavigate();
  const detailHref = row.id ? `/vehicle/${encodeURIComponent(row.id)}` : null;

  const openDetail = (e) => {
    if (!detailHref) return;
    // Ignore clicks that originated from a real anchor / button inside the row
    const tag = e.target.closest("a,button");
    if (tag && tag.dataset.rowNav !== "true") return;
    if (e.metaKey || e.ctrlKey || e.button === 1) {
      window.open(detailHref, "_blank");
      return;
    }
    navigate(detailHref);
  };

  return (
    <div
      data-testid="compat-row"
      onClick={openDetail}
      role={detailHref ? "link" : undefined}
      tabIndex={detailHref ? 0 : undefined}
      onKeyDown={(e) => {
        if (detailHref && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          navigate(detailHref);
        }
      }}
      className="group grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-4 hover:bg-neutral-900/50 transition cursor-pointer focus:outline-none focus:bg-neutral-900/60"
    >
      {/* Manufacturer */}
      <div className={grouped ? "hidden" : "md:col-span-3 lg:col-span-2 flex items-center gap-3"}>
        {row.manufacturer_logo && (
          <img
            src={row.manufacturer_logo}
            alt={row.manufacturer}
            className="h-16 w-16 object-contain rounded bg-panel border border-neutral-800 p-1.5"
            loading="lazy"
          />
        )}
        <div>
          <div className="font-medium text-neutral-100">
            <Highlight text={row.manufacturer} query={search} />
          </div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">
            <VehicleTypeIcon type={row.vehicle_type} className="inline-block h-4 w-5 mr-1 align-[-2px]" />
            {vehicleTypeLabel(row.vehicle_type)}
          </div>
        </div>
      </div>
      {/* Model */}
      <div className={grouped ? "md:col-span-2" : "md:col-span-2 lg:col-span-1"}>
        <div className="text-neutral-100">
          {grouped ? (row.version || "—") : (model ? <Highlight text={model} query={search} /> : "—")}
        </div>
        <div className="text-xs text-neutral-500">{row.year}</div>
        <NewBadge firstSeen={row.first_seen} className="mt-1" />
      </div>
      {/* Engine */}
      <div className="md:col-span-2 lg:col-span-4 text-neutral-200">
        {row.engine ? <Highlight text={row.engine} query={search} /> : "—"}
        {row.fuel && (
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-0.5">
            {fuelLabel(row.fuel)}
          </div>
        )}
      </div>
      {/* ECU/TCU */}
      <div className="md:col-span-2">
        {ecu.url ? (
          <a
            href={ecu.url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            data-testid="ecu-link"
            className="group/ecu inline-flex flex-col text-neutral-100 hover:text-[#E13636] transition"
          >
            <span className="flex items-center gap-1">
              <Highlight text={ecu.name} query={search} />
              <ExternalLink className="w-3 h-3 opacity-0 group-hover/ecu:opacity-100 transition" />
            </span>
            <span
              className="text-[11px] text-neutral-400 font-mono mt-0.5"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              <Highlight text={ecu.protocol?.label} query={search} />
            </span>
          </a>
        ) : (
          <div className="text-neutral-100">
            {ecu.name ? <Highlight text={ecu.name} query={search} /> : "—"}
          </div>
        )}
      </div>
      {/* Type */}
      <div className="md:col-span-1">
        {ecu.type?.label && (
          <Badge
            variant="outline"
            className="border-neutral-700 text-neutral-300 bg-panel text-[10px] font-mono"
          >
            {ecu.type.label}
          </Badge>
        )}
      </div>
      {/* Methods */}
      <div className="md:col-span-2 flex flex-wrap gap-1.5 items-start relative">
        {(row.methods || []).map((m, i) => (
          <a
            key={i}
            href={m.url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            data-testid={`method-link-${m.label}`}
            title={m.label}
            className="inline-flex items-center hover:opacity-75 transition"
          >
            <MethodGlyph label={m.label} className="w-9 h-9" />
          </a>
        ))}
        {detailHref && (
          <div className="ml-auto self-center opacity-0 group-hover:opacity-100 transition text-[#D32222]">
            <ArrowRight className="w-4 h-4" data-testid="detail-arrow" />
          </div>
        )}
      </div>
    </div>
  );
}
